import { useState } from 'react';
import type { CSSProperties, KeyboardEvent, Ref } from 'react';

/**
 * 비밀번호 입력 칸 + **보이기 토글** (2026-09-14 사용자 요청).
 *
 * 🔴 왜 필요한가: 접속 비밀번호는 **남이 불러 주는 값을 받아 적는다.** 화면에 점만 보이면
 * "제대로 들어갔는지" 확인할 방법이 없고, 틀리면 돌아오는 것은 "접속이 거부되었습니다" 뿐이라
 * 오타인지 비밀번호가 바뀐 것인지 구분할 수 없다. 로그인 비밀번호(내가 아는 값)와 성질이 다르다.
 *
 * 비밀번호 칸이 앱·뷰어 곳곳에 있어(접속·코드 접속·익명 접속·무인 PW 설정·로그인) **한 곳에서 만든다** —
 * 각자 눈 아이콘을 붙이면 어느 하나는 반드시 빠진다.
 *
 * ⚠️ 보이기는 **그 순간 그 화면에서만** 이다. 값은 어디에도 저장하지 않고, 칸을 벗어나면(재마운트)
 * 다시 가려진 상태로 시작한다 — 어깨너머로 남는 노출을 최소화한다.
 */
export function PasswordField({
  value,
  onChange,
  placeholder,
  autoFocus,
  style,
  onKeyDown,
  disabled,
  'aria-label': ariaLabel,
  inputRef,
}: {
  /** 입력 칸 참조(다른 칸에서 여기로 포커스를 넘길 때 — 예: 최근 코드를 고르면 비밀번호 칸으로). */
  inputRef?: Ref<HTMLInputElement>;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  style?: CSSProperties;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  'aria-label'?: string;
}) {
  const [shown, setShown] = useState(false);

  return (
    <span className="pw-field" style={{ position: 'relative', display: 'inline-flex', ...style }}>
      <input
        ref={inputRef}
        // 보일 때도 `type=text` 로 바꾸는 대신 같은 칸을 유지한다 — 타입을 바꾸면 브라우저·WebView 가
        // 커서 위치를 잃고(맨 뒤로 점프) 입력 중이던 사람이 흐름을 놓친다.
        type={shown ? 'text' : 'password'}
        value={value}
        autoFocus={autoFocus}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        // 오른쪽 끝은 토글 버튼 자리 — 긴 값이 버튼 아래로 숨지 않게 여백을 준다.
        style={{ width: '100%', paddingRight: 34 }}
      />
      <button
        type="button"
        className="ghost pw-toggle"
        // 마우스를 누르는 순간 입력 칸이 포커스를 잃지 않게 한다(누르고 나서 계속 타이핑할 수 있어야 한다).
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setShown((s) => !s)}
        title={shown ? '비밀번호 가리기' : '비밀번호 보이기'}
        aria-label={shown ? '비밀번호 가리기' : '비밀번호 보이기'}
        style={{
          position: 'absolute',
          right: 2,
          top: '50%',
          transform: 'translateY(-50%)',
          padding: '2px 6px',
          fontSize: 13,
          lineHeight: 1,
          border: 'none',
          background: 'transparent',
        }}
      >
        {shown ? '🙈' : '👁'}
      </button>
    </span>
  );
}
